import React, { useState, useEffect, useRef } from 'react';
import { 
  Clock, CheckCircle, FileText, Download, Upload,
  User, Phone, Mail, Rocket, Shield, RefreshCw, MessageCircle,
  Send, AlertTriangle, Eye, Banknote, LogOut, Menu, X
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../../components/ui/dialog';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { toast } from 'sonner';
import api from '../../lib/api';
import { formatDate, getWhatsAppLink } from '../../lib/utils';
import { useAuth } from '../../context/AuthContext';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_a01f8d12-1bde-4fd2-8a5f-4dafd263008a/artifacts/d665bj3h_Global%20Wings%20fond%20noir.png";
const AGENT_WHATSAPP = '+243842217277';

const statusConfig = {
  initiated: { label: 'Initiée', color: 'bg-gray-500/20 text-gray-400', icon: Clock, step: 1 },
  received: { label: 'Paiement reçu', color: 'bg-blue-500/20 text-blue-400', icon: CheckCircle, step: 2 },
  launched: { label: 'Docs téléchargés', color: 'bg-yellow-500/20 text-yellow-400', icon: Rocket, step: 3 },
  processed: { label: 'Visa prêt', color: 'bg-purple-500/20 text-purple-400', icon: Shield, step: 4 },
  completed: { label: 'Terminée', color: 'bg-green-500/20 text-green-400', icon: CheckCircle, step: 5 }
};

export const AgentDashboard = () => {
  const { user, logout } = useAuth();
  const [stats, setStats] = useState(null);
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedApp, setSelectedApp] = useState(null);
  const [showDetail, setShowDetail] = useState(false);
  const [newNote, setNewNote] = useState('');
  const [uploading, setUploading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [showWhatsAppPrompt, setShowWhatsAppPrompt] = useState(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const visaUploadRef = useRef(null);
  const insuranceUploadRef = useRef(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [statsRes, appsRes] = await Promise.all([
        api.get('/agent/stats'),
        api.get('/agent/my-applications')
      ]);
      setStats(statsRes.data);
      setApplications(appsRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Erreur de chargement');
    } finally {
      setLoading(false);
    }
  };

  const refreshSelectedApp = async (appId) => {
    try {
      const response = await api.get(`/visa-applications/${appId}`);
      setSelectedApp(response.data);
      fetchData();
    } catch (error) {
      console.error('Error refreshing app:', error);
    }
  };

  // WhatsApp messages
  const getWhatsAppMessages = (app) => {
    const ref = app?.id?.slice(0, 8).toUpperCase();
    const downloadLink = `${window.location.origin}/visa/dubai/download/${app?.id}`;
    
    return {
      agentNewApplication: `📥 *NOUVELLE DEMANDE VISA DUBAÏ*\n\nRéférence: ${ref}\nClient: ${app?.applicant_name}\nTéléphone: ${app?.applicant_phone}\nType: ${app?.visa_type || 'Visa Visiteur'}\n\n💳 Paiement confirmé\n\nGlobal Wings Travel Consulting`,
      clientVisaReady: `🎉 *VISA PRÊT - Global Wings*\n\nBonjour ${app?.applicant_name},\n\nVotre visa pour Dubaï est prêt !\n\n📋 Référence: ${ref}\n\n📥 Téléchargez vos documents:\n${downloadLink}\n\n✅ Visa électronique\n✅ Assurance voyage\n\nBon voyage ! ✈️\nGlobal Wings Travel Consulting`
    };
  };

  const confirmPayment = async () => {
    if (!selectedApp) return;
    try {
      await api.patch(`/visa-applications/${selectedApp.id}/confirm-payment?payment_method=cash`);
      toast.success('Paiement confirmé !');
      setShowWhatsAppPrompt('payment_confirmed');
      refreshSelectedApp(selectedApp.id);
    } catch (error) {
      toast.error('Erreur');
    }
  };

  const downloadClientDocs = async () => {
    if (!selectedApp) return;
    setDownloading(true);
    try {
      const response = await api.get(
        `/visa-applications/${selectedApp.id}/download-client-docs`,
        { responseType: 'blob' }
      );
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `documents_${selectedApp.id.slice(0,8).toUpperCase()}.zip`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success('Documents téléchargés !');
      refreshSelectedApp(selectedApp.id);
    } catch (error) {
      toast.error('Erreur');
    } finally {
      setDownloading(false);
    }
  };

  const handleAgentUpload = async (file, documentType) => {
    if (!selectedApp || !file) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const response = await api.post(
        `/visa-applications/${selectedApp.id}/agent-upload?document_type=${documentType}`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      
      if (response.data.status_updated) {
        toast.success('Documents prêts ! Envoyez le lien au client');
        setShowWhatsAppPrompt('visa_ready');
      } else {
        toast.success(`${documentType === 'visa' ? 'Visa' : 'Assurance'} uploadé(e)`);
      }
      refreshSelectedApp(selectedApp.id);
    } catch (error) {
      toast.error('Erreur');
    } finally {
      setUploading(false);
    }
  };

  const addNote = async () => {
    if (!newNote.trim() || !selectedApp) return;
    try {
      await api.post(`/visa-applications/${selectedApp.id}/notes?note=${encodeURIComponent(newNote)}`);
      toast.success('Note ajoutée');
      setNewNote('');
      refreshSelectedApp(selectedApp.id);
    } catch (error) {
      toast.error('Erreur');
    }
  };

  const isDubaiVisa = (app) => app?.destination?.toLowerCase().includes('duba');
  
  const getAgentDocs = (app) => {
    const docs = app?.agent_documents || [];
    return {
      visa: docs.find(d => d.document_type === 'visa'),
      insurance: docs.find(d => d.document_type === 'insurance')
    };
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#020617] flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-sky-500 border-t-transparent"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#020617]">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-[#0F172A] border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src={LOGO_URL} alt="Global Wings" className="h-20" />
          </div>
          
          <div className="flex items-center gap-4">
            <div className="hidden md:block text-right">
              <p className="text-sm text-white">{user?.full_name}</p>
              <p className="text-xs text-gray-400">Agent</p>
            </div>
            <Button onClick={logout} variant="ghost" size="sm" className="text-gray-400 hover:text-white">
              <LogOut className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-6 space-y-6">
        {/* Welcome & Stats */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-white">Bonjour, {user?.full_name?.split(' ')[0]} 👋</h1>
            <p className="text-gray-400">Voici vos dossiers assignés</p>
          </div>
          <Button onClick={fetchData} variant="outline" className="border-white/10 text-gray-300">
            <RefreshCw className="w-4 h-4 mr-2" /> Actualiser
          </Button>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="bg-[#0F172A] border-white/10">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-sky-500/20 flex items-center justify-center">
                  <FileText className="w-5 h-5 text-sky-400" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-white">{stats?.total_assigned || 0}</p>
                  <p className="text-xs text-gray-400">Total assignés</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-[#0F172A] border-white/10">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-yellow-500/20 flex items-center justify-center">
                  <Clock className="w-5 h-5 text-yellow-400" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-white">{stats?.pending || 0}</p>
                  <p className="text-xs text-gray-400">En cours</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-[#0F172A] border-white/10">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-green-500/20 flex items-center justify-center">
                  <CheckCircle className="w-5 h-5 text-green-400" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-white">{stats?.completed || 0}</p>
                  <p className="text-xs text-gray-400">Terminés</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-[#0F172A] border-white/10">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-purple-500/20 flex items-center justify-center">
                  <Rocket className="w-5 h-5 text-purple-400" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-white">{stats?.today_assigned || 0}</p>
                  <p className="text-xs text-gray-400">Aujourd'hui</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Applications List */}
        <Card className="bg-[#0F172A] border-white/10">
          <CardHeader>
            <CardTitle className="text-white">Mes Dossiers</CardTitle>
          </CardHeader>
          <CardContent>
            {applications.length === 0 ? (
              <div className="text-center py-12 text-gray-400">
                <FileText className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p>Aucun dossier assigné</p>
                <p className="text-sm mt-2">Les dossiers vous seront assignés par l'administrateur</p>
              </div>
            ) : (
              <div className="space-y-3">
                {applications.map((app) => {
                  const StatusIcon = statusConfig[app.status]?.icon || Clock;
                  const isDubai = isDubaiVisa(app);
                  return (
                    <div 
                      key={app.id}
                      className="p-4 rounded-xl bg-white/5 hover:bg-white/10 transition-colors cursor-pointer border border-transparent hover:border-white/10"
                      onClick={() => { setSelectedApp(app); setShowDetail(true); setShowWhatsAppPrompt(null); }}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                            isDubai ? 'bg-amber-500/20' : 'bg-sky-500/20'
                          }`}>
                            <StatusIcon className={`w-6 h-6 ${statusConfig[app.status]?.color.split(' ')[1]}`} />
                          </div>
                          <div>
                            <div className="flex items-center gap-2">
                              <p className="font-medium text-white">{app.applicant_name}</p>
                              {isDubai && <Badge className="bg-amber-500/20 text-amber-400 text-[10px]">Dubaï</Badge>}
                            </div>
                            <p className="text-sm text-gray-400">{app.destination} • {app.visa_type}</p>
                            <p className="text-xs text-gray-500">Réf: {app.id?.slice(0, 8).toUpperCase()}</p>
                          </div>
                        </div>
                        <div className="flex flex-col items-end gap-2">
                          <Badge className={statusConfig[app.status]?.color}>
                            {statusConfig[app.status]?.label}
                          </Badge>
                          <span className="text-xs text-gray-500">{formatDate(app.created_at)}</span>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>
      </main>

      {/* Detail Modal */}
      <Dialog open={showDetail} onOpenChange={setShowDetail}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto bg-[#0F172A] border-white/10 text-white">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-3">
              <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
                isDubaiVisa(selectedApp) ? 'bg-amber-500/20' : 'bg-sky-500/20'
              }`}>
                <FileText className={`w-5 h-5 ${isDubaiVisa(selectedApp) ? 'text-amber-400' : 'text-sky-400'}`} />
              </div>
              Dossier {selectedApp?.id?.slice(0, 8).toUpperCase()}
            </DialogTitle>
            <DialogDescription className="text-gray-400">
              {selectedApp?.destination} - {selectedApp?.visa_type}
            </DialogDescription>
          </DialogHeader>

          {selectedApp && (
            <div className="space-y-4 py-4">
              {/* Status Progress */}
              {isDubaiVisa(selectedApp) && (
                <div className="p-3 rounded-lg bg-amber-500/10 border border-amber-500/20">
                  <div className="flex items-center justify-between mb-2">
                    {[1,2,3,4,5].map((step) => {
                      const isActive = step <= statusConfig[selectedApp.status]?.step;
                      return (
                        <div key={step} className="flex items-center">
                          <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                            isActive ? 'bg-amber-500 text-white' : 'bg-white/10 text-gray-500'
                          }`}>{step}</div>
                          {step < 5 && <div className={`w-6 h-0.5 ${isActive ? 'bg-amber-500' : 'bg-white/10'}`} />}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Workflow Actions */}
              {isDubaiVisa(selectedApp) && (
                <div className="space-y-3">
                  {/* Confirm Payment */}
                  {selectedApp.status === 'initiated' && (
                    <Button onClick={confirmPayment} className="w-full bg-blue-600 hover:bg-blue-700">
                      <Banknote className="w-4 h-4 mr-2" /> Confirmer Paiement Espèces
                    </Button>
                  )}

                  {/* WhatsApp after payment */}
                  {showWhatsAppPrompt === 'payment_confirmed' && selectedApp.status === 'received' && (
                    <div className="p-3 rounded-lg bg-green-500/10 border border-green-500/20 space-y-2">
                      <p className="text-sm text-green-400">✅ Paiement confirmé !</p>
                      <a href={getWhatsAppLink(AGENT_WHATSAPP, getWhatsAppMessages(selectedApp).agentNewApplication)}
                        target="_blank" rel="noopener noreferrer"
                        className="flex items-center justify-center gap-2 w-full py-2 rounded-lg bg-green-600 hover:bg-green-700 text-white text-sm">
                        <MessageCircle className="w-4 h-4" /> Notifier l'équipe (WhatsApp)
                      </a>
                      <button onClick={() => setShowWhatsAppPrompt(null)} className="w-full text-xs text-gray-400">Passer</button>
                    </div>
                  )}

                  {/* Download docs */}
                  {selectedApp.status === 'received' && !showWhatsAppPrompt && (
                    <Button onClick={downloadClientDocs} disabled={downloading} className="w-full bg-yellow-600 hover:bg-yellow-700">
                      {downloading ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
                      Télécharger Documents Client
                    </Button>
                  )}

                  {/* Upload visa/insurance */}
                  {selectedApp.status === 'launched' && !showWhatsAppPrompt && (
                    <div className="space-y-2">
                      <input ref={visaUploadRef} type="file" className="hidden" accept=".pdf,.jpg,.jpeg,.png"
                        onChange={(e) => handleAgentUpload(e.target.files[0], 'visa')} />
                      <input ref={insuranceUploadRef} type="file" className="hidden" accept=".pdf,.jpg,.jpeg,.png"
                        onChange={(e) => handleAgentUpload(e.target.files[0], 'insurance')} />
                      
                      {getAgentDocs(selectedApp).visa ? (
                        <div className="p-2 rounded bg-green-500/10 text-green-400 text-sm flex items-center gap-2">
                          <CheckCircle className="w-4 h-4" /> Visa uploadé
                        </div>
                      ) : (
                        <Button onClick={() => visaUploadRef.current?.click()} disabled={uploading} className="w-full bg-purple-600 hover:bg-purple-700">
                          <Upload className="w-4 h-4 mr-2" /> Uploader VISA
                        </Button>
                      )}
                      
                      {getAgentDocs(selectedApp).insurance ? (
                        <div className="p-2 rounded bg-green-500/10 text-green-400 text-sm flex items-center gap-2">
                          <CheckCircle className="w-4 h-4" /> Assurance uploadée
                        </div>
                      ) : (
                        <Button onClick={() => insuranceUploadRef.current?.click()} disabled={uploading} variant="outline" className="w-full border-purple-500/50 text-purple-400">
                          <Upload className="w-4 h-4 mr-2" /> Uploader ASSURANCE
                        </Button>
                      )}
                    </div>
                  )}

                  {/* WhatsApp after upload */}
                  {showWhatsAppPrompt === 'visa_ready' && selectedApp.status === 'processed' && (
                    <div className="p-3 rounded-lg bg-green-500/10 border border-green-500/20 space-y-2">
                      <p className="text-sm text-green-400">✅ Visa & Assurance prêts !</p>
                      <a href={getWhatsAppLink(selectedApp.applicant_phone, getWhatsAppMessages(selectedApp).clientVisaReady)}
                        target="_blank" rel="noopener noreferrer"
                        className="flex items-center justify-center gap-2 w-full py-2 rounded-lg bg-green-600 hover:bg-green-700 text-white text-sm">
                        <MessageCircle className="w-4 h-4" /> Envoyer lien au client
                      </a>
                      <button onClick={() => setShowWhatsAppPrompt(null)} className="w-full text-xs text-gray-400">Passer</button>
                    </div>
                  )}

                  {/* Waiting */}
                  {selectedApp.status === 'processed' && !showWhatsAppPrompt && (
                    <div className="p-3 rounded-lg bg-green-500/10 border border-green-500/20 space-y-2">
                      <p className="text-sm text-green-400">✅ En attente téléchargement client</p>
                      <a href={getWhatsAppLink(selectedApp.applicant_phone, getWhatsAppMessages(selectedApp).clientVisaReady)}
                        target="_blank" rel="noopener noreferrer"
                        className="flex items-center justify-center gap-2 w-full py-2 rounded-lg bg-white/10 hover:bg-white/20 text-white text-sm">
                        <Send className="w-4 h-4" /> Renvoyer lien WhatsApp
                      </a>
                    </div>
                  )}

                  {/* Completed */}
                  {selectedApp.status === 'completed' && (
                    <div className="p-3 rounded-lg bg-green-500/10 border border-green-500/20">
                      <p className="text-sm text-green-400">✅ Dossier terminé</p>
                    </div>
                  )}
                </div>
              )}

              {/* Client Info */}
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div className="flex items-center gap-2">
                  <User className="w-4 h-4 text-gray-400" />
                  <span className="text-white">{selectedApp.applicant_name}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Phone className="w-4 h-4 text-gray-400" />
                  <span className="text-white">{selectedApp.applicant_phone}</span>
                </div>
                <div className="flex items-center gap-2 col-span-2">
                  <Mail className="w-4 h-4 text-gray-400" />
                  <span className="text-white">{selectedApp.applicant_email}</span>
                </div>
              </div>

              {/* Notes */}
              <div className="space-y-2">
                <Label className="text-gray-400 text-xs">Notes</Label>
                {selectedApp.notes?.length > 0 && (
                  <div className="space-y-1 max-h-24 overflow-y-auto">
                    {selectedApp.notes.map((note, idx) => (
                      <div key={idx} className="p-2 rounded bg-white/5 text-xs">
                        <p className="text-white">{note.content}</p>
                        <p className="text-gray-500 mt-1">{note.author} • {formatDate(note.created_at)}</p>
                      </div>
                    ))}
                  </div>
                )}
                <div className="flex gap-2">
                  <Textarea placeholder="Ajouter une note..." value={newNote}
                    onChange={(e) => setNewNote(e.target.value)}
                    className="bg-white/5 border-white/10 text-white text-sm" rows={2} />
                  <Button onClick={addNote} className="bg-sky-500 hover:bg-sky-600 px-4">+</Button>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AgentDashboard;
